import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { PaymentMethod } from '../payment-method';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import {
  getBillingAddress,
  getClientTokenResponse,
  getPaymentDetails,
  updateBillingAddress,
  updateEditBillingAddressEditMode
} from '../../checkout/shop-checkout';
import { PaymentDetails, PaymentType } from '../../checkout/shop-checkout.interface';
import './ideal-button';
import { waitForElementsToLoad } from '../../../common/utils/delay-util';
import { IdealDomId } from './interfaces/ideal.interfaces';

@customElement('mhecomm-ideal-payment-method')
export class IdealPaymentMethod extends PaymentMethod {
  @state()
  private idealPaymentDetails?: PaymentDetails;

  stateChanged(state: StoreState): void {
    this.updateIsSelected(PaymentType.BRAINTREE_IDEAL, state);
    this.idealPaymentDetails = getPaymentDetails(state);
    this.shippable = false;
    this.showUseShippingAddressButton = false;
  }

  renderComp() {
    if (!this.isSelected) {
      return nothing;
    }

    return html`
      ${this.hasIdealNonce()
        ? this.renderBillingAddressSection()
        : this.renderIdealButton()
      }
    `;
  }

  renderIdealButton() {
    const clientTokenResponse = getClientTokenResponse(store.getState() as StoreState);

    if (!clientTokenResponse) {
      return html`<div>Loading payment options...</div>`;
    }

    return html`
      <div id="ideal-button">
        <mhecomm-ideal-button
          clientToken='${clientTokenResponse?.token}'
          merchantAccountId='${clientTokenResponse?.idealMerchantId || ''}'
          amount='${this.totalAmount}'
        >
        </mhecomm-ideal-button>
      </div>
    `;
  }

  renderBillingAddressSection() {
    return html`
      <span class="visually-hidden" id="${IdealDomId.SELECTED_PAYMENT_METHOD}">Payment method: iDEAL</span>
      <span class="visually-hidden" id="${IdealDomId.SELECTED_PAYMENT_FORM}">Enter your Billing Address</span>
      <div
        id="${IdealDomId.BILLING_CONTAINER}"
        tabindex="-1"
        aria-labelledby="${IdealDomId.SELECTED_PAYMENT_METHOD}"
        aria-describedby="${IdealDomId.SELECTED_PAYMENT_FORM}"
      >
        ${this.renderBillingAddressForm()}
      </div>
    `;
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(_changedProperties);

    if (_changedProperties.has('idealPaymentDetails')) {
      const oldNonce = _changedProperties.get('idealPaymentDetails') as PaymentDetails | undefined;
      if (this.hasIdealNonce() && !oldNonce?.paymentNonce) {
        this.focusBillingAddressContainer();
      }
    }
  }

  private focusBillingAddressContainer() {
    const doFocus = () => {
      // requestAnimationFrame ensures DOM layout is ready to accept focus
      requestAnimationFrame(() => {
        const container = this.shadowRoot?.querySelector(`#${IdealDomId.BILLING_CONTAINER}`) as HTMLElement;
        container?.focus();
      });
    };

    if (document.hasFocus()) {
      doFocus();
    } else {
      window.addEventListener('focus', doFocus, { once: true });
      // Safety fallback: in case the 'focus' event already fired or is missed
      setTimeout(doFocus, 500);
    }
  }

  public async validatePaymentMethod(): Promise<void>{
    const paymentDetails = getPaymentDetails(store.getState() as StoreState);
    if (
      paymentDetails?.paymentType !== PaymentType.BRAINTREE_IDEAL ||
      !paymentDetails?.paymentNonce
    ) {
      throw new Error('Ideal payment failed.');
    }

    const addressForm = this.getAddressForm();
    await addressForm.validate()
      .then(addressValidationInfo => {
        if (addressValidationInfo) {
          store.dispatch(updateBillingAddress(addressValidationInfo));
        }
      });
  }

  paymentMethodEditAction(): void {
    store.dispatch(updateEditBillingAddressEditMode(true));
    waitForElementsToLoad(this.shadowRoot!, ['mhecomm-address-form']).then(() => {
      this.getAddressForm().prepopulate(getBillingAddress(store.getState() as StoreState)!);
      this.billingAddress = getBillingAddress(store.getState() as StoreState)!;
    });
  }

  hasIdealNonce() {
    return this.idealPaymentDetails?.paymentNonce && this.idealPaymentDetails?.paymentType === PaymentType.BRAINTREE_IDEAL;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-ideal-payment-method': IdealPaymentMethod;
  }
}